import { supabase } from './supabase';
import type { Agent, AgentTask, AgentResponsibility, CharacterAppearance, CharacterBaseType, CharacterProfile, SafetyState, WorkspaceMembership } from '../types';

/**
 * Read-only AgentOrdinary workspace adapter for the private Supabase project.
 * Canonical SALRI workforce writes and runtime controls remain in agentopia.ts.
 * Postgres RLS checks workspace access on every table queried here.
 */
const obj = (value: unknown): Record<string, any> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : {};

async function userId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  if (!data.user?.id) throw new Error('Sign in to AgentOrdinary to view your workspace.');
  return data.user.id;
}

export async function getPrimaryMembership(): Promise<WorkspaceMembership> {
  const id = await userId();
  const { data, error } = await supabase.from('agentopia_workspace_memberships')
    .select('workspace_id,role')
    .eq('auth_user_id', id).eq('enabled', true)
    .order('created_at', { ascending: true }).limit(1).maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('No enabled AgentOrdinary workspace membership exists for this login.');
  return { workspace_id: String(data.workspace_id), role: data.role as WorkspaceMembership['role'] };
}

export async function listAgents(workspaceId: string): Promise<Agent[]> {
  await userId();
  const { data, error } = await supabase.from('agentopia_agents')
    .select('id,workspace_id,role_key,official_name,nickname,title,department,status,enabled,profile_state,current_version,revision,profile,created_at,updated_at')
    .eq('workspace_id', workspaceId).order('official_name');
  if (error) throw error;
  return (data ?? []).map((row: any): Agent => ({
    id: String(row.id), workspace_id: String(row.workspace_id), role_key: String(row.role_key || ''),
    official_name: String(row.official_name || row.role_key || 'Agent'),
    nickname: row.nickname ? String(row.nickname) : null,
    title: String(row.title || row.role_key || 'AI Employee'),
    department: row.department ? String(row.department) : null,
    status: String(row.status || (row.enabled ? 'idle' : 'paused')),
    enabled: row.enabled === true,
    profile_state: String(row.profile_state || 'draft'),
    current_version: Number(row.current_version || 1), revision: Number(row.revision || 0),
    profile: obj(row.profile),
  }));
}

export async function listCharacterProfiles(workspaceId: string): Promise<CharacterProfile[]> {
  await userId();
  const { data, error } = await supabase.from('agentopia_character_profiles')
    .select('id,workspace_id,agent_id,base_type,transparent_background,status,appearance,created_at,updated_at')
    .eq('workspace_id', workspaceId).order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row: any): CharacterProfile => ({
    id: String(row.id), workspace_id: String(row.workspace_id), agent_id: String(row.agent_id),
    base_type: row.base_type as CharacterBaseType, transparent_background: row.transparent_background !== false,
    status: row.status === 'ready' ? 'ready' : 'draft', appearance: obj(row.appearance) as CharacterAppearance,
    created_at: String(row.created_at), updated_at: String(row.updated_at),
  }));
}

export async function getSafetyState(workspaceId: string): Promise<SafetyState> {
  await userId();
  const { data, error } = await supabase.from('agentopia_safety_state')
    .select('workspace_id,armed,global_paused,pause_reason,tripped_at,tripped_by,last_event_at,max_identical_failures,max_identical_tests,hard_error_immediate,updated_at')
    .eq('workspace_id', workspaceId).maybeSingle();
  if (error) throw error;
  if (!data) {
    return { workspace_id: workspaceId, armed: true, global_paused: true,
      pause_reason: 'Workspace safety state is not initialized.', tripped_at: null, tripped_by: null,
      last_event_at: null, max_identical_failures: 0, max_identical_tests: 0,
      hard_error_immediate: true, updated_at: new Date(0).toISOString() };
  }
  return {
    workspace_id: String(data.workspace_id), armed: data.armed !== false,
    global_paused: data.global_paused === true, pause_reason: data.pause_reason ? String(data.pause_reason) : null,
    tripped_at: data.tripped_at ? String(data.tripped_at) : null,
    tripped_by: data.tripped_by ? String(data.tripped_by) : null,
    last_event_at: data.last_event_at ? String(data.last_event_at) : null,
    max_identical_failures: Number(data.max_identical_failures || 0),
    max_identical_tests: Number(data.max_identical_tests || 0),
    hard_error_immediate: data.hard_error_immediate !== false, updated_at: String(data.updated_at),
  };
}

export async function listTasks(workspaceId: string): Promise<AgentTask[]> {
  await userId();
  const { data, error } = await supabase.from('agentopia_tasks')
    .select('id,workspace_id,agent_id,title,objective,status,priority,requires_approval,blocker,scheduled_at,started_at,completed_at,attempt_count,max_attempts,last_error,source_system,source_id,metadata,created_at,updated_at')
    .eq('workspace_id', workspaceId).order('updated_at', { ascending: false }).limit(100);
  if (error) throw error;
  return (data ?? []).map((row: any): AgentTask => ({
    id: String(row.id), workspace_id: String(row.workspace_id),
    agent_id: row.agent_id ? String(row.agent_id) : null,
    title: String(row.title || 'Task'), objective: row.objective ? String(row.objective) : null,
    status: String(row.status || 'queued'), priority: Number(row.priority ?? 50),
    requires_approval: row.requires_approval === true, blocker: row.blocker ? String(row.blocker) : null,
    scheduled_at: row.scheduled_at ? String(row.scheduled_at) : null,
    started_at: row.started_at ? String(row.started_at) : null,
    completed_at: row.completed_at ? String(row.completed_at) : null,
    attempt_count: Number(row.attempt_count || 0), max_attempts: Number(row.max_attempts || 3),
    last_error: row.last_error ? String(row.last_error) : null,
    source_system: String(row.source_system || 'agentordinary'),
    source_id: row.source_id ? String(row.source_id) : null, metadata: obj(row.metadata),
    created_at: String(row.created_at), updated_at: String(row.updated_at),
  }));
}

export async function listResponsibilities(workspaceId: string): Promise<AgentResponsibility[]> {
  await userId();
  const { data, error } = await supabase.from('agentopia_responsibilities')
    .select('id,workspace_id,agent_id,responsibility_key,name,objective,mode,status,priority,source_trigger_id,current_action,next_action,resume_condition,checkpoint,last_task_id,last_started_at,last_completed_at,next_review_at,max_parallel,metadata,created_at,updated_at')
    .eq('workspace_id', workspaceId).order('priority', { ascending: false })
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((row: any): AgentResponsibility => ({
    id: String(row.id), workspace_id: String(row.workspace_id), agent_id: String(row.agent_id),
    responsibility_key: String(row.responsibility_key), name: String(row.name),
    objective: String(row.objective), mode: String(row.mode), status: String(row.status),
    priority: Number(row.priority || 50),
    source_trigger_id: row.source_trigger_id ? String(row.source_trigger_id) : null,
    current_action: row.current_action ? String(row.current_action) : null,
    next_action: row.next_action ? String(row.next_action) : null,
    resume_condition: row.resume_condition ? String(row.resume_condition) : null,
    checkpoint: row.checkpoint ? String(row.checkpoint) : null,
    last_task_id: row.last_task_id ? String(row.last_task_id) : null,
    last_started_at: row.last_started_at ? String(row.last_started_at) : null,
    last_completed_at: row.last_completed_at ? String(row.last_completed_at) : null,
    next_review_at: row.next_review_at ? String(row.next_review_at) : null,
    max_parallel: Number(row.max_parallel || 1), metadata: obj(row.metadata),
    created_at: String(row.created_at), updated_at: String(row.updated_at),
  }));
}
